<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Add Raise Complaint
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.complaint_type_id"
                :items="complaintTypes"
                item-text="description"
                item-value="id"
                outlined
                label="Complaint Type *"
                hide-details="auto"
                prepend-inner-icon="mdi-format-list-bulleted"
                :error-messages="errors.complaint_type_id"
                required
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.store_id"
                :items="stores"
                item-text="business_name"
                item-value="id"
                outlined
                label="Store (Optional)"
                hide-details="auto"
                prepend-inner-icon="mdi-store"
                clearable
                :error-messages="errors.store_id"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description"
                hide-details="auto"
                prepend-inner-icon="mdi-text"
                rows="4"
                :error-messages="errors.description"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-file-input
                v-model="form.documents"
                outlined
                multiple
                label="Upload Documents (Images, PDF, DOC)"
                accept="image/*,application/pdf,.doc,.docx"
                prepend-inner-icon="mdi-file-multiple"
                show-size
                :error-messages="errors.documents"
              >
                <template v-slot:selection>
                  <v-chip
                    v-if="form.documents && form.documents.length > 0"
                    color="primary"
                    text-color="white"
                    small
                  >
                    {{ form.documents.length }} file(s) selected
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row v-if="form.documents && form.documents.length > 0">
            <v-col sm="12">
              <v-card outlined>
                <v-card-title class="py-2">
                  <v-icon class="mr-2">mdi-file-multiple</v-icon>
                  Selected Documents
                </v-card-title>
                <v-card-text class="pt-0">
                  <v-chip
                    v-for="(file, index) in form.documents"
                    :key="index"
                    class="ma-1"
                    close
                    @click:close="removeDocument(index)"
                  >
                    <v-icon left>{{ getFileIcon(file.type) }}</v-icon>
                    {{ file.name }}
                    <small class="ml-2">({{ formatFileSize(file.size) }})</small>
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading || !form.description || !form.complaint_type_id"
            @click="save"
          >
            Save Raise Complaint
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "RaiseComplaintCreatePage",
  data() {
    return {
      form: {
        complaint_type_id: null,
        store_id: null,
        description: "",
        documents: [],
      },
      complaintTypes: [],
      stores: [],
      errors: {},
      isLoading: false,
    };
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        // Get complaint types and stores
        const response = await axios.get("/raise_complaints/masters");
        this.complaintTypes = response.data.complaint_types || [];
        
        // Get stores if needed (you might need a separate endpoint)
        const storesResponse = await axios.get("/stores");
        this.stores = storesResponse.data.data || [];
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};
        
        // Client-side validation
        if (!this.form.complaint_type_id) {
          this.errors.complaint_type_id = ['Complaint type is required'];
          this.isLoading = false;
          return;
        }
        
        if (!this.form.description) {
          this.errors.description = ['Description is required'];
          this.isLoading = false;
          return;
        }
        
        // Create FormData for multipart upload
        const formData = new FormData();
        
        // Add form fields
        formData.append('description', this.form.description);
        formData.append('complaint_type_id', this.form.complaint_type_id);
        if (this.form.store_id) {
          formData.append('store_id', this.form.store_id);
        }
        
        // Add documents
        if (this.form.documents && this.form.documents.length > 0) {
          this.form.documents.forEach((file) => {
            formData.append('doc_path[]', file);
          });
        }
        
        await axios.post("/raise_complaints", formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.$toast.success("Raise Complaint created successfully!", {
          position: "top-right",
          timeout: 3000,
        });
        
        this.isLoading = false;
        this.$router.back();
      } catch (error) {
        this.isLoading = false;
        
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        }
        
        this.$toast.error("Error creating complaint. Please try again.", {
          position: "top-right",
          timeout: 3000,
        });
        
        console.error("Error:", error);
      }
    },
    
    removeDocument(index) {
      this.form.documents.splice(index, 1);
    },
    
    getFileIcon(mimeType) {
      if (mimeType.startsWith('image/')) {
        return 'mdi-file-image';
      } else if (mimeType === 'application/pdf') {
        return 'mdi-file-pdf-box';
      } else if (mimeType.includes('word') || mimeType.includes('document')) {
        return 'mdi-file-word';
      }
      return 'mdi-file-document';
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
  },
};
</script>
